import React, { useState, useEffect } from "react";
import { useContext } from "react";
import { SalesContext } from "../context/SalesContext";
import {
  FaBell, FaCalendarAlt, FaBoxOpen, FaFileInvoiceDollar, FaUsers,
  FaMobileAlt, FaCreditCard, FaMoneyBillWave, FaWallet, FaTrophy,
  FaArrowUp, FaArrowDown, FaPlus, FaBox, FaChartBar, FaCloudDownloadAlt,
  FaExclamationTriangle, FaFileInvoice, FaShieldAlt, FaCheckCircle, FaRocket,
  FaChartLine, FaBolt, FaBullseye
} from "react-icons/fa";

import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { getShopId } from "../utils/shop";

import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from "recharts";

import "../styles/Dashboard.css";
import { useNavigate } from "react-router-dom";

// Helper component for the Sparkline graphs in the top stat cards
const Sparkline = ({ color, trend }) => {
  const isDown = trend === 'down';
  const pathData = isDown 
    ? "M0,10 C20,10 40,25 60,15 C80,25 100,35 100,35" 
    : "M0,30 C20,15 40,30 60,10 C80,20 100,0 100,0";
  const gradId = `grad-${color.replace('#', '')}`;

  return (
    <svg width="100%" height="35" viewBox="0 0 100 35" preserveAspectRatio="none" className="sparkline-svg">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={pathData} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d={`${pathData} L100,35 L0,35 Z`} fill={`url(#${gradId})`} stroke="none" />
    </svg>
  );
};

const parseInvoiceDate = (invoice) => {
  if (!invoice || !invoice.createdAt) return null;
  if (typeof invoice.createdAt.toDate === "function") {
    return invoice.createdAt.toDate();
  }
  if (invoice.createdAt.seconds) {
    return new Date(invoice.createdAt.seconds * 1000);
  }
  return new Date(invoice.createdAt);
};

function Dashboard() {
  const navigate = useNavigate();

  const [singleDate, setSingleDate] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [invoices, setInvoices] = useState([]);

  // 1. Fetch Invoices from DB
  useEffect(() => {
    const loadInvoices = async () => {
      try {
        const shopId = getShopId();
        const snapshot = await getDocs(collection(db, "shops", shopId, "invoices"));
        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setInvoices(data);
      } catch (error) {
        console.error("Error loading invoices:", error);
      }
    };
    loadInvoices();
  }, []);

  // 2. Load Filters and Handle Custom Calendar Events
  useEffect(() => {
    const loadFilterDates = () => {
      const from = sessionStorage.getItem("dashboardFromDate");
      const to = sessionStorage.getItem("dashboardToDate");
      const single = sessionStorage.getItem("dashboardSingleDate");

      const parseStorageDate = (dateStr) => {
        if (!dateStr) return null;
        if (dateStr.includes("/")) {
          const parts = dateStr.split("/");
          if (parts.length === 3) {
            return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
          }
        }
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? null : d;
      };

      if (!from && !to && !single) {
        const today = new Date();
        setFromDate(today);
        setToDate(today);
        setSingleDate(today);
        return;
      }

      setFromDate(parseStorageDate(from));
      setToDate(parseStorageDate(to));
      setSingleDate(parseStorageDate(single));
    };

    loadFilterDates();
    window.addEventListener("dateFilterChanged", loadFilterDates);
    return () => {
      window.removeEventListener("dateFilterChanged", loadFilterDates);
    };
  }, []);

  // 3. Filter Invoices based on Date Context
  const filteredInvoices = invoices.filter((invoice) => {
    const invoiceDate = parseInvoiceDate(invoice);
    if (!invoiceDate || isNaN(invoiceDate.getTime())) return false;

    if (fromDate && toDate && fromDate.getTime() !== toDate.getTime()) {
      const startDate = new Date(fromDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(toDate);
      endDate.setHours(23, 59, 59, 999);
      return invoiceDate >= startDate && invoiceDate <= endDate;
    }

    const targetDay = singleDate ? new Date(singleDate) : (fromDate ? new Date(fromDate) : null);
    if (targetDay) {
      const selectedDay = new Date(targetDay);
      selectedDay.setHours(0, 0, 0, 0);
      const nextDay = new Date(selectedDay);
      nextDay.setDate(nextDay.getDate() + 1);
      return invoiceDate >= selectedDay && invoiceDate < nextDay;
    }
    return true; 
  });

  // 4. Graph and Calculations Preparation
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const salesData = weekDays.map((day) => {
    const dayInvoices = filteredInvoices.filter((invoice) => {
      const invDate = parseInvoiceDate(invoice);
      if (!invDate) return false;
      return weekDays[invDate.getDay()] === day;
    });

    const totalSales = dayInvoices.reduce((sum, invoice) => sum + Number(invoice.grandTotal || 0), 0);

    return {
      day,
      sales: totalSales,
      bills: dayInvoices.length,
      avgBill: dayInvoices.length > 0 ? totalSales / dayInvoices.length : 0,
    };
  });

  const DONUT_COLORS = ["#16a34a", "#2563eb", "#ea580c"]; // Green, Blue, Orange
  const recentInvoices = [...filteredInvoices].reverse().slice(0, 5);

  const totalSales = filteredInvoices.reduce((sum, invoice) => sum + Number(invoice.grandTotal || 0), 0);
  const totalGST = filteredInvoices.reduce((sum, invoice) => sum + Number(invoice.cgst || 0) + Number(invoice.sgst || 0), 0);
  const cashCollection = filteredInvoices.filter(inv => inv.paymentMethod === "Cash").reduce((sum, inv) => sum + Number(inv.grandTotal || 0), 0);
  const upiCollection = filteredInvoices.filter(inv => inv.paymentMethod === "UPI").reduce((sum, inv) => sum + Number(inv.grandTotal || 0), 0);
  const cardCollection = filteredInvoices.filter(inv => inv.paymentMethod === "Card").reduce((sum, inv) => sum + Number(inv.grandTotal || 0), 0);

  const paymentData = [
    { name: "Cash", value: cashCollection },
    { name: "UPI", value: upiCollection },
    { name: "Card", value: cardCollection },
  ];

  const products = JSON.parse(localStorage.getItem("products")) || [];
  const lowStockProducts = products.filter(product => Number(product.units) < 10);
  const totalCustomers = filteredInvoices.length;

  const productSales = {};
  filteredInvoices.forEach((invoice) => {
    if (!invoice.items) return;
    invoice.items.forEach((item) => {
      const productName = item.name || item.productName;
      const qty = Number(item.quantity || item.qty || 0);
      productSales[productName] = (productSales[productName] || 0) + qty;
    });
  });

  const topProducts = Object.entries(productSales).sort((a, b) => b[1] - a[1]).slice(0, 5);

  const customerSummary = {};
  filteredInvoices.forEach((invoice) => {
    const customer = invoice.customer || invoice.customerName || "Walk-in Customer";
    customerSummary[customer] = (customerSummary[customer] || 0) + Number(invoice.grandTotal || 0);
  });

  const topCustomer = Object.entries(customerSummary).sort((a, b) => b[1] - a[1])[0];
  const highestInvoice = filteredInvoices.length ? Math.max(...filteredInvoices.map(inv => Number(inv.grandTotal || 0))) : 0;
  const recentCustomer = recentInvoices[0]?.customer || recentInvoices[0]?.customerName || "N/A";

  const recentActivities = recentInvoices.map((invoice) => {
    const invDate = parseInvoiceDate(invoice);
    return {
      type: "invoice",
      message: `New invoice created`,
      time: invDate ? invDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : "N/A",
      details: invoice.invoiceNo
    };
  });

  return (
    <div className="modern-dashboard">
      
      {/* MAIN LAYOUT WRAPPER */}
      <div className="dashboard-grid-layout">
        
        {/* ================= LEFT COLUMN ================= */}
        <div className="dashboard-left-col">
          
          {/* --- TOP STATS ROW (7 items) --- */}
          <div className="stats-header-grid">
            <div className="stat-box">
              <div className="stat-box-top">
                <div className="stat-icon green-bg"><FaMoneyBillWave /></div>
                <div className="stat-titles">
                  <p>TODAY'S SALES</p>
                  <h3>₹{totalSales.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</h3>
                </div>
              </div>
              <p className="stat-trend positive"><FaArrowUp /> 12.5% vs yesterday</p>
              <Sparkline color="#16a34a" trend="up" />
            </div>

            <div className="stat-box">
              <div className="stat-box-top">
                <div className="stat-icon blue-bg"><FaFileInvoiceDollar /></div>
                <div className="stat-titles">
                  <p>INVOICES</p>
                  <h3>{filteredInvoices.length}</h3>
                </div>
              </div>
              <p className="stat-trend positive"><FaArrowUp /> 6.3% vs yesterday</p>
              <Sparkline color="#2563eb" trend="up" />
            </div>

            <div className="stat-box">
              <div className="stat-box-top">
                <div className="stat-icon purple-bg"><FaUsers /></div>
                <div className="stat-titles">
                  <p>TOTAL CUSTOMERS</p>
                  <h3>{totalCustomers}</h3>
                </div>
              </div>
              <p className="stat-trend positive"><FaArrowUp /> 8.1% vs last week</p>
              <Sparkline color="#9333ea" trend="up" />
            </div>

            <div className="stat-box">
              <div className="stat-box-top">
                <div className="stat-icon red-bg"><FaBoxOpen /></div>
                <div className="stat-titles">
                  <p>LOW STOCK</p>
                  <h3>{lowStockProducts.length}</h3>
                </div>
              </div>
              <p className="stat-trend negative"><FaArrowDown /> 0%</p>
              <Sparkline color="#dc2626" trend="down" />
            </div>

            <div className="stat-box">
              <div className="stat-box-top">
                <div className="stat-icon green-bg"><FaWallet /></div>
                <div className="stat-titles">
                  <p>CASH</p>
                  <h3>₹{cashCollection.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</h3>
                </div>
              </div>
              <p className="stat-trend positive"><FaArrowUp /> 15.4% vs yesterday</p>
              <Sparkline color="#16a34a" trend="up" />
            </div>

            <div className="stat-box">
              <div className="stat-box-top">
                <div className="stat-icon light-blue-bg"><FaMobileAlt /></div>
                <div className="stat-titles">
                  <p>UPI</p>
                  <h3>₹{upiCollection.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</h3>
                </div>
              </div>
              <p className="stat-trend neutral">— 0% vs yesterday</p>
              <Sparkline color="#0891b2" trend="up" />
            </div>

            {/* ADDED CARD STAT BOX */}
            <div className="stat-box">
              <div className="stat-box-top">
                <div className="stat-icon orange-light-bg"><FaCreditCard /></div>
                <div className="stat-titles">
                  <p>CARD</p>
                  <h3>₹{cardCollection.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</h3>
                </div>
              </div>
              <p className="stat-trend neutral">— 0% vs yesterday</p>
              <Sparkline color="#ea580c" trend="up" />
            </div>
          </div>

          {/* --- CHARTS ROW --- */}
          <div className="charts-row">
            <div className="chart-container">
              <div className="section-header">
                <h3>Weekly Sales Trend</h3>
                <select className="filter-dropdown"><option>This Week</option></select>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={salesData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#888'}} />
                  <YAxis axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#888'}} />
                  <Tooltip cursor={{ fill: 'transparent' }} />
                  <Line type="monotone" dataKey="sales" stroke="#2563eb" strokeWidth={3} dot={{r: 4, fill: "#2563eb"}} activeDot={{r: 6}} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-container">
              <div className="section-header">
                <h3>Payment Distribution</h3>
                <select className="filter-dropdown"><option>This Month</option></select>
              </div>
              <div className="donut-wrapper">
                <ResponsiveContainer width="50%" height={220}>
                  <PieChart>
                    <Pie data={paymentData} innerRadius={60} outerRadius={85} paddingAngle={2} dataKey="value">
                      {paymentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="donut-legend">
                  <div className="legend-item"><span className="dot green"></span><div><p>Cash</p><strong>₹{cashCollection.toLocaleString()}</strong></div></div>
                  <div className="legend-item"><span className="dot blue"></span><div><p>UPI</p><strong>₹{upiCollection.toLocaleString()}</strong></div></div>
                  <div className="legend-item"><span className="dot orange"></span><div><p>Card</p><strong>₹{cardCollection.toLocaleString()}</strong></div></div>
                </div>
              </div>
            </div>

            <div className="chart-container">
              <div className="section-header">
                <h3>Invoice Activity</h3>
                <select className="filter-dropdown"><option>This Week</option></select>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={salesData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#888'}} />
                  <YAxis axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#888'}} />
                  <Tooltip cursor={{fill: '#f8fafc'}} />
                  <Bar dataKey="bills" fill="#16a34a" radius={[4, 4, 0, 0]} maxBarSize={30} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* --- FINANCIALS ROW --- */}
          <div className="financials-row">
            <div className="financial-card">
              <div className="section-header">
                <h3>
                  <div className="icon-circle-small bg-cyan text-white"><FaBullseye /></div>
                  Sales Target
                </h3>
                <select className="filter-dropdown"><option>This Month</option></select>
              </div>
              <p className="fin-label">Monthly Target</p>
              <h3 className="fin-amount">₹{totalSales.toLocaleString()} / ₹10,00,000</h3>
              <div className="progress-row">
                <div className="progress-bar-bg"><div className="progress-bar-fill" style={{width: '45%'}}></div></div>
                <span className="progress-text">45%</span>
              </div>
              <p className="fin-label blue-text">45% Completed</p>
            </div>

            <div className="financial-card">
              <div className="section-header">
                <h3>
                  <div className="icon-circle-small purple-light"><FaBox className="text-purple" /></div>
                  Inventory Summary
                </h3>
              </div>
              <div className="fin-list">
                <div className="fin-row"><p>Total Products</p><strong>250</strong></div>
                <div className="fin-row"><p><span className="dot orange"></span> Low Stock</p><strong>{lowStockProducts.length}</strong></div>
                <div className="fin-row"><p><span className="dot red"></span> Out of Stock</p><strong>0</strong></div>
                <div className="fin-row fin-border-top"><p>Inventory Value</p><strong>₹2,45,760</strong></div>
              </div>
            </div>

            <div className="financial-card">
              <div className="section-header">
                <h3>
                  <div className="icon-circle-small bg-green text-white"><FaChartBar /></div>
                  Profit & Loss
                </h3>
              </div>
              <div className="fin-list">
                <div className="fin-row"><p>Revenue</p><strong>₹4,50,000</strong></div>
                <div className="fin-row"><p>Expenses</p><strong className="text-red">₹1,80,000</strong></div>
                <div className="fin-row fin-border-top"><p>Profit</p><strong className="text-green">₹2,70,000</strong></div>
                <div className="fin-row"><p>Profit Margin</p><strong>60%</strong></div>
              </div>
            </div>

            <div className="financial-card">
              <div className="section-header">
                <h3>
                  <div className="icon-circle-small bg-blue text-white"><FaWallet /></div>
                  Cash Flow
                </h3>
              </div>
              <div className="fin-list">
                <div className="fin-row"><p>Money In</p><strong className="text-green">₹4,50,000</strong></div>
                <div className="fin-row"><p>Money Out</p><strong className="text-red">₹1,80,000</strong></div>
                <div className="fin-row fin-border-top"><p>Balance</p><strong className="text-blue">₹2,70,000</strong></div>
              </div>
            </div>
          </div>

          {/* --- RECENT INVOICES & ALERTS ROW --- */}
          <div className="bottom-tables-row">
            <div className="table-card">
              <div className="section-header">
                <h3>Recent Invoices</h3>
                <button className="view-all-btn">View All Invoices</button>
              </div>
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {recentInvoices.map((invoice) => {
                    const invDate = parseInvoiceDate(invoice);
                    return (
                      <tr key={invoice.invoiceNo}>
                        <td className="text-blue font-semibold">{invoice.invoiceNo}</td>
                        <td>{invDate ? invDate.toLocaleDateString() : "N/A"}</td>
                        <td>{invoice.customerName || invoice.customer || "Walk-in"}</td>
                        <td className="font-semibold">₹{Number(invoice.grandTotal || 0).toFixed(2)}</td>
                        <td><span className="badge-paid">PAID</span></td>
                        <td><span className={`badge-payment ${invoice.paymentMethod?.toLowerCase() || 'cash'}`}>{invoice.paymentMethod || 'Cash'}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="alerts-card">
               <div className="section-header">
                <h3><FaBell className="icon-red" /> Alerts & Notifications</h3>
                <button className="view-all-btn">View All</button>
              </div>
              <div className="alert-list">
                <div className="alert-item">
                  <div className="icon-circle red-light"><FaExclamationTriangle className="text-red" /></div>
                  <div className="alert-info"><h4>Low Stock Items</h4><p>{lowStockProducts.length} items need attention</p></div>
                  <div className="alert-badge red">{lowStockProducts.length}</div>
                </div>
                <div className="alert-item">
                  <div className="icon-circle blue-light"><FaFileInvoice className="text-blue" /></div>
                  <div className="alert-info"><h4>Pending Payments</h4><p>5 invoices pending</p></div>
                  <div className="alert-badge blue">5</div>
                </div>
                <div className="alert-item">
                  <div className="icon-circle purple-light"><FaShieldAlt className="text-purple" /></div>
                  <div className="alert-info"><h4>License Expiry</h4><p>245 days remaining</p></div>
                  <div className="alert-badge purple">245</div>
                </div>
                <div className="alert-item">
                  <div className="icon-circle green-light"><FaCheckCircle className="text-green" /></div>
                  <div className="alert-info"><h4>Backup Successful</h4><p>Last backup: Today 01:30 PM</p></div>
                  <div className="alert-badge green-tick">✔</div>
                </div>
                <div className="alert-item">
                  <div className="icon-circle blue-light"><FaRocket className="text-blue" /></div>
                  <div className="alert-info"><h4>New Update Available</h4><p>Version 2.4.1 is ready</p></div>
                  <button className="update-btn">Update</button>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* ================= RIGHT COLUMN (SIDEBAR) ================= */}
        <div className="dashboard-right-col">
          
          <div className="widget-card">
            <div className="section-header">
              <h3><FaTrophy className="icon-orange" /> Top Selling Products</h3>
              <button className="view-all-btn">View All</button>
            </div>
            <div className="top-products-list">
              {topProducts.length === 0 ? <p className="text-muted">No sales data</p> : 
                topProducts.map(([name, qty], index) => (
                  <div key={index} className="product-list-item">
                    <div className="prod-left">
                      <span className="rank-circle">{index + 1}</span>
                      <p>{name}</p>
                    </div>
                    <strong>{qty} Sold</strong>
                  </div>
                ))
              }
            </div>
          </div>

          <div className="widget-card">
            <div className="section-header">
              <h3>
                <div className="icon-circle-small purple-light"><FaUsers className="text-purple" /></div> 
                Customer Summary
              </h3>
              <button className="view-all-btn">View All</button>
            </div>
            <div className="summary-list">
              <div className="summary-row"><p>Top Customer</p><strong>{topCustomer?.[0] || "Walk-in Customer"}</strong></div>
              <div className="summary-row"><p>Highest Invoice</p><strong>₹{highestInvoice.toFixed(2)}</strong></div>
              <div className="summary-row"><p>Recent Customer</p><strong>{recentCustomer}</strong></div>
              <div className="summary-row"><p>Total Customers</p><strong>{totalCustomers}</strong></div>
            </div>
          </div>

          <div className="widget-card">
            <div className="section-header">
              <h3><FaBolt className="icon-purple" /> Quick Actions</h3>
            </div>
            <div className="quick-actions-grid">
              <button onClick={() => navigate("/billing")} className="action-btn btn-blue"><FaPlus /> New Invoice</button>
              <button onClick={() => navigate("/add-product")} className="action-btn btn-orange"><FaBox /> Add Product</button>
              <button onClick={() => navigate("/products")} className="action-btn btn-purple"><FaBoxOpen /> Inventory</button>
              <button onClick={() => navigate("/customers")} className="action-btn btn-indigo"><FaUsers /> Customers</button>
              <button onClick={() => navigate("/reports")} className="action-btn btn-orange"><FaChartBar /> Reports</button>
              <button className="action-btn btn-pink"><FaFileInvoiceDollar /> Expenses</button>
              <button className="action-btn btn-blue"><FaChartLine /> Sales</button>
              <button className="action-btn btn-green"><FaCloudDownloadAlt /> Backup</button>
            </div>
          </div>

          <div className="widget-card">
            <div className="section-header">
              <h3>Recent Activity</h3>
              <button className="view-all-btn">View All</button>
            </div>
            <div className="activity-timeline">
              {recentActivities.length === 0 ? <p className="text-muted">No activity</p> : 
                recentActivities.slice(0, 4).map((activity, index) => (
                  <div key={index} className="timeline-item">
                    <div className="icon-circle-small blue-light"><FaFileInvoice className="text-blue" /></div>
                    <div className="timeline-content">
                      <div className="time-row">
                        <span className="time-col">{activity.time}</span>
                      </div>
                      <p className="timeline-title">{activity.message}</p>
                      <p className="timeline-desc">{activity.details}</p>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default Dashboard;